import React, { useState } from "react";

export const ContactPage: React.FC = () => {
  const [sending, setSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [statusType, setStatusType] = useState<"success" | "error" | null>(
    null,
  );

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setStatusMessage(null);
    setStatusType(null);

    const form = e.currentTarget;
    const formData = new FormData(form);
    const serviceValue = String(formData.get("service") || "");
    const serviceLabelMap: Record<string, string> = {
      residential: "Residential Cleaning",
      commercial: "Commercial/Office Cleaning",
      industrial: "Industrial Cleaning",
      "deep-clean": "One-off Deep Clean",
      other: "Other Inquiry",
    };

    formData.append(
      "subject",
      `Website Enquiry: ${serviceLabelMap[serviceValue] || "General Inquiry"}`,
    );

    try {
      const response = await fetch("/contact-form.php", {
        method: "POST",
        body: formData,
      });

      const responseText = await response.text();

      if (!response.ok || responseText.trim() !== "OK") {
        throw new Error(
          responseText || "Unable to send your message right now.",
        );
      }

      form.reset();
      setStatusType("success");
      setStatusMessage("Your message has been sent successfully.");
    } catch (error) {
      setStatusType("error");
      setStatusMessage(
        error instanceof Error
          ? error.message
          : "Something went wrong while sending your message.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-background text-on-surface font-body-md selection:bg-secondary-container selection:text-on-secondary-container overflow-x-hidden">
      <main className="max-w-container-max mx-auto px-md sm:px-lg py-lg sm:py-xl overflow-x-hidden">
        <section className="relative bg-brand-gradient py-lg sm:py-xl px-md overflow-hidden rounded-lg text-center text-on-primary mb-lg">
          <h1 className="font-display-lg text-3xl sm:text-display-lg leading-tight">
            Let’s Start Fresh
          </h1>
          <p className="font-body-md sm:font-body-lg opacity-90 max-w-2xl mx-auto mt-sm">
            Professional cleaning standards for your home or business. Reach out
            today for a sparkling tomorrow.
          </p>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-lg sm:gap-xl min-w-0">
          <div className="lg:col-span-5 space-y-md sm:space-y-lg min-w-0">
            <div className="space-y-sm sm:space-y-md">
              <h2 className="font-headline-lg text-headline-md sm:text-headline-lg text-primary">
                Get in Touch
              </h2>
              <p className="font-body-sm sm:font-body-md text-on-surface-variant">
                We operate throughout the Limpopo area, delivering clinical
                hygiene and professional maintenance.
              </p>
            </div>

            <div className="grid gap-sm sm:gap-md min-w-0">
              <div className="flex items-center gap-sm sm:gap-md p-sm sm:p-md bg-surface-container-low rounded-lg transition-all hover:bg-surface-container w-full min-w-0 overflow-hidden">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-gradient flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-primary text-[18px] sm:text-base">
                    call
                  </span>
                </div>
                <div className="min-w-0 flex flex-col gap-1">
                  <p className="font-label-md text-[11px] sm:text-label-md text-secondary uppercase leading-none">
                    Call Us
                  </p>
                  <a
                    className="font-headline-md text-[12px] sm:text-headline-md leading-tight break-words max-w-[11rem] sm:max-w-none hover:text-primary transition-colors"
                    href="tel:+27679376048"
                  >
                    +27 67 937 6048
                  </a>
                  <a
                    className="font-headline-md text-[12px] sm:text-headline-md leading-tight break-words max-w-[11rem] sm:max-w-none hover:text-primary transition-colors"
                    href="tel:0781668330"
                  >
                    078 166 8330
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-sm sm:gap-md p-sm sm:p-md bg-surface-container-low rounded-lg transition-all hover:bg-surface-container w-full min-w-0 overflow-hidden">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-gradient flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-primary text-[18px] sm:text-base">
                    mail
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-label-md text-[11px] sm:text-label-md text-secondary uppercase leading-none">
                    Email Us
                  </p>
                  <p className="font-headline-md text-[12px] sm:text-headline-md leading-tight break-all sm:break-words max-w-[11rem] sm:max-w-none">
                    kolomaakacleaning@gmail.com
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-sm sm:gap-md p-sm sm:p-md bg-surface-container-low rounded-lg transition-all hover:bg-surface-container w-full min-w-0 overflow-hidden">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-brand-gradient flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-on-primary text-[18px] sm:text-base">
                    language
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-label-md text-[11px] sm:text-label-md text-secondary uppercase leading-none">
                    Website
                  </p>
                  <p className="font-headline-md text-[12px] sm:text-headline-md leading-tight break-all sm:break-words max-w-[11rem] sm:max-w-none">
                    www.kolomakacleaning.co.za
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 bg-white rounded-xl p-sm sm:p-md md:p-lg border border-outline-variant shadow-sm">
            <h3 className="font-headline-lg text-headline-md sm:text-headline-lg mb-md sm:mb-lg text-primary">
              Service Inquiry Form
            </h3>
            <form onSubmit={handleSubmit} className="space-y-md">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                <div className="space-y-xs">
                  <label
                    className="font-label-md text-on-surface-variant"
                    htmlFor="email"
                  >
                    Email Address
                  </label>
                  <input
                    className="w-full px-md py-sm rounded border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none bg-surface-bright"
                    id="email"
                    name="email"
                    placeholder="john@example.com"
                    required
                    type="email"
                  />
                </div>
                <div className="space-y-xs">
                  <label
                    className="font-label-md text-on-surface-variant"
                    htmlFor="name"
                  >
                    Full Name
                  </label>
                  <input
                    className="w-full px-md py-sm rounded border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none bg-surface-bright"
                    id="name"
                    name="name"
                    placeholder="John Doe"
                    required
                    type="text"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-md">
                <div className="space-y-xs">
                  <label
                    className="font-label-md text-on-surface-variant"
                    htmlFor="phone"
                  >
                    Phone Number
                  </label>
                  <input
                    className="w-full px-md py-sm rounded border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none bg-surface-bright"
                    id="phone"
                    name="phone"
                    placeholder="+27 00 000 0000"
                    type="tel"
                  />
                </div>
                <div className="space-y-xs">
                  <label
                    className="font-label-md text-on-surface-variant"
                    htmlFor="service"
                  >
                    Service Type
                  </label>
                  <select
                    className="w-full px-md py-sm rounded border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none bg-surface-bright"
                    id="service"
                    name="service"
                  >
                    <option value="residential">Residential Cleaning</option>
                    <option value="commercial">
                      Commercial/Office Cleaning
                    </option>
                    <option value="industrial">Industrial Cleaning</option>
                    <option value="deep-clean">One-off Deep Clean</option>
                    <option value="other">Other Inquiry</option>
                  </select>
                </div>
              </div>

              <div className="space-y-xs">
                <label
                  className="font-label-md text-on-surface-variant"
                  htmlFor="message"
                >
                  Your Message
                </label>
                <textarea
                  className="w-full px-md py-sm rounded border border-outline-variant focus:border-primary focus:ring-1 focus:ring-primary transition-all outline-none bg-surface-bright resize-none"
                  id="message"
                  name="message"
                  placeholder="How can we help you achieve a cleaner space?"
                  required
                  rows={5}
                ></textarea>
              </div>

              <div className="flex items-center gap-sm pt-sm">
                <input
                  className="w-5 h-5 rounded border-outline-variant text-secondary focus:ring-secondary"
                  id="privacy"
                  required
                  type="checkbox"
                />
                <label
                  className="font-body-md text-on-surface-variant"
                  htmlFor="privacy"
                >
                  I agree to the privacy policy regarding my contact data.
                </label>
              </div>

              <button
                className="w-full md:w-auto bg-brand-gradient text-on-primary font-headline-md px-xl py-sm rounded hover:brightness-110 transition-all flex items-center justify-center gap-sm group"
                type="submit"
                disabled={sending}
              >
                {sending ? (
                  <>
                    <span className="material-symbols-outlined animate-spin">
                      sync
                    </span>{" "}
                    Sending...
                  </>
                ) : (
                  <>
                    Send Message{" "}
                    <span className="material-symbols-outlined group-hover:translate-x-1 transition-transform">
                      send
                    </span>
                  </>
                )}
              </button>

              {statusMessage && (
                <p
                  className={
                    statusType === "success"
                      ? "text-green-700 font-medium"
                      : "text-red-600 font-medium"
                  }
                  role="status"
                >
                  {statusMessage}
                </p>
              )}
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContactPage;
